import { jsx } from "react/jsx-runtime";
import React__default, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { AnalyticsId } from "./constants.js";
import { Breakpoint, useBreakpoint } from "./hooks/useBreakpoint.js";
import { useIsVisible } from "./hooks/useIsVisible.js";
import { useLayoutEvent } from "./hooks/useLayoutEvent.js";
import { getAriaLabelAttributes } from "./utils/accessibility.js";
import { H as HorizontalScroll } from "./HorizontalScroll.js";
import tableStyles from "./components/Table/styles.module.scss";
import { isTouchDevice } from "./utils/device.js";
const configUsesCss = (config) => config.variant === ResponsiveTableVariant.centeredoverflow || config.variant === ResponsiveTableVariant.block;
const mapConfigToClass = (config) => config.variant === "centeredoverflow" || config.variant === "block" ? tableStyles[`table--${config.variant}-${config.breakpoint}`] : "";
const sortByBreakpointsDescending = (a, b) => Breakpoint[a.breakpoint] - Breakpoint[b.breakpoint];
const isValidForCurrentBreakpoint = (config, breakpoint) => Breakpoint[config.breakpoint] >= breakpoint;
const getConfigForBreakpoint = (config, breakpoint) => {
  if (Array.isArray(config)) {
    config.sort(sortByBreakpointsDescending);
    return config.find((x) => isValidForCurrentBreakpoint(x, breakpoint));
  } else if (config && isValidForCurrentBreakpoint(config, breakpoint)) {
    return config;
  }
};
const getCurrentConfig = (config, breakpoint, tableWidth, windowWidth) => {
  const breakpointConfig = getConfigForBreakpoint(config, breakpoint);
  const canUseHorizontalScroll = isTouchDevice();
  const canUseCenteredOverflow = tableWidth <= windowWidth;
  if (!breakpointConfig) {
    return;
  }
  if (breakpointConfig.variant === ResponsiveTableVariant.centeredoverflow && !canUseCenteredOverflow && breakpointConfig.fallbackVariant === ResponsiveTableVariant.horizontalscroll) {
    return {
      variant: canUseHorizontalScroll ? ResponsiveTableVariant.horizontalscroll : ResponsiveTableVariant.none,
      breakpoint: breakpointConfig.breakpoint
    };
  }
  if (breakpointConfig.variant === ResponsiveTableVariant.centeredoverflow && !canUseCenteredOverflow && breakpointConfig.fallbackVariant !== ResponsiveTableVariant.centeredoverflow) {
    return {
      variant: breakpointConfig.fallbackVariant ?? ResponsiveTableVariant.none,
      breakpoint: breakpointConfig.breakpoint
    };
  }
  if (breakpointConfig.variant === ResponsiveTableVariant.horizontalscroll && !canUseHorizontalScroll && breakpointConfig.fallbackVariant === ResponsiveTableVariant.centeredoverflow) {
    return {
      variant: canUseCenteredOverflow ? ResponsiveTableVariant.centeredoverflow : ResponsiveTableVariant.none,
      breakpoint: breakpointConfig.breakpoint
    };
  }
  if (breakpointConfig.variant === ResponsiveTableVariant.horizontalscroll && !canUseHorizontalScroll && breakpointConfig.fallbackVariant !== ResponsiveTableVariant.horizontalscroll) {
    return {
      variant: breakpointConfig.fallbackVariant ?? ResponsiveTableVariant.none,
      breakpoint: breakpointConfig.breakpoint
    };
  }
  return breakpointConfig;
};
const getBreakpointClass = (config) => config && configUsesCss(config) ? mapConfigToClass(config) : void 0;
const getCenteredOverflowTableStyle = (parentWidth, tableWidth) => {
  if (parentWidth >= tableWidth) {
    return;
  }
  const COLUMN_GUTTER_WIDTH_PX = 8;
  return { left: `${(parentWidth - tableWidth) / 2 - COLUMN_GUTTER_WIDTH_PX}px` };
};
var ResponsiveTableVariant = /* @__PURE__ */ ((ResponsiveTableVariant2) => {
  ResponsiveTableVariant2["none"] = "none";
  ResponsiveTableVariant2["centeredoverflow"] = "centeredoverflow";
  ResponsiveTableVariant2["horizontalscroll"] = "horizontalscroll";
  ResponsiveTableVariant2["block"] = "block";
  return ResponsiveTableVariant2;
})(ResponsiveTableVariant || {});
var ModeType = /* @__PURE__ */ ((ModeType2) => {
  ModeType2["compact"] = "compact";
  ModeType2["normal"] = "normal";
  return ModeType2;
})(ModeType || {});
const defaultConfig = [
  {
    breakpoint: "xl",
    variant: "centeredoverflow",
    fallbackVariant: "horizontalscroll"
    /* horizontalscroll */
  }
];
const simpleConfig = [
  {
    breakpoint: "xl",
    variant: "centeredoverflow",
    fallbackVariant: "horizontalscroll"
    /* horizontalscroll */
  },
  {
    breakpoint: "sm",
    variant: "centeredoverflow",
    fallbackVariant: "block"
    /* block */
  }
];
const Table = ({
  id,
  testId,
  className,
  children,
  breakpointConfig = defaultConfig,
  mode = "normal",
  scrollAriaLabel,
  scrollAriaLabelledById,
  ...rest
}) => {
  const [currentConfig, setCurrentConfig] = useState();
  const [tableWidth, setTableWidth] = useState(0);
  const [parentWidth, setParentWidth] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const tableRef = useRef(null);
  const tableIsVisible = useIsVisible(tableRef, 0);
  const breakpoint = useBreakpoint();
  useEffect(() => {
    setCurrentConfig(getCurrentConfig(breakpointConfig, breakpoint, tableWidth, windowWidth));
  }, [breakpointConfig, breakpoint, tableWidth, windowWidth]);
  useEffect(() => {
    var _a, _b, _c;
    if ((currentConfig == null ? void 0 : currentConfig.variant) === "centeredoverflow" || (currentConfig == null ? void 0 : currentConfig.variant) === "horizontalscroll") {
      setTableWidth(((_a = tableRef.current) == null ? void 0 : _a.getBoundingClientRect().width) ?? 0);
    }
    if ((currentConfig == null ? void 0 : currentConfig.variant) === "centeredoverflow") {
      setParentWidth(((_c = (_b = tableRef.current) == null ? void 0 : _b.parentElement) == null ? void 0 : _c.getBoundingClientRect().width) ?? 0);
    }
  }, [currentConfig, breakpoint]);
  useLayoutEvent(() => setWindowWidth(window.innerWidth), ["resize"], 100);
  useEffect(() => {
    var _a;
    if (tableWidth === 0 && tableIsVisible) {
      setTableWidth(((_a = tableRef.current) == null ? void 0 : _a.getBoundingClientRect().width) ?? 0);
    }
  }, [tableWidth, tableIsVisible]);
  const tableStyle = (currentConfig == null ? void 0 : currentConfig.variant) === "centeredoverflow" ? getCenteredOverflowTableStyle(parentWidth, tableWidth) : void 0;
  const breakpointClass = getBreakpointClass(currentConfig);
  const tableClass = classNames(tableStyles.table, breakpointClass, className);
  const table = /* @__PURE__ */ jsx(
    "table",
    {
      className: tableClass,
      id,
      "data-testid": testId,
      "data-analyticsid": AnalyticsId.Table,
      ref: tableRef,
      style: tableStyle,
      ...rest,
      children: React__default.Children.map(children, (child) => React__default.isValidElement(child) && React__default.cloneElement(child, { mode }))
    }
  );
  const ariaLabelAttributes = getAriaLabelAttributes({ label: scrollAriaLabel, id: scrollAriaLabelledById });
  if ((currentConfig == null ? void 0 : currentConfig.variant) === "horizontalscroll") {
    return /* @__PURE__ */ jsx(HorizontalScroll, { childWidth: tableWidth, testId: "horizontal-scroll", ...ariaLabelAttributes, children: table });
  }
  return table;
};
export {
  ModeType as M,
  ResponsiveTableVariant as R,
  Table as T,
  defaultConfig as d,
  simpleConfig as s
};
//# sourceMappingURL=Table.js.map
